/**
 * Lazy loading utilities for React components
 * Implements code splitting and lazy loading with error boundaries
 */

import React, { lazy, Suspense } from 'react';
import BrandLoader from '../components/BrandLoader';

// Loading fallback — the SAME brand loader the menu uses for its data fetch, so
// the chunk-load phase and the fetch phase render identical DOM (one loader).
const LoadingFallback = () => <BrandLoader />;

// Error boundary component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Lazy loading error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Something went wrong</h2>
            <p className="text-gray-600 mb-4">Failed to load component</p>
            <button
              onClick={() => window.location.reload()}
              className="px-5 py-2.5 bg-ent-green text-white rounded-md hover:bg-ent-green-700 transition-colors"
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Lazy load wrapper function
export const lazyLoad = (importFunc, fallback = <LoadingFallback />) => {
  const LazyComponent = lazy(importFunc);

  return (props) => (
    <ErrorBoundary>
      <Suspense fallback={fallback}>
        <LazyComponent {...props} />
      </Suspense>
    </ErrorBoundary>
  );
};

// Preload component function
export const preloadComponent = (importFunc) => {
  // This will trigger the import but not use the result
  // The component will be cached for when it's actually needed
  importFunc();
};

// Export default loading fallback
export { LoadingFallback };